import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  forwardRef,
  memo,
  useEffect,
} from "react";
import Select, { ActionMeta, SelectInstance } from "react-select";
import useCategory from "../../hooks/useCategory";
import { customSearchCategorySelectStyles } from "../../../Search/customSelectStyles";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentLang } from "../../../../store/selectors/appSelectors";
import DropdownIndicator from "../DropdownIndicator/DropdownIndicator";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import { LangE } from "../../../../models/enums";
import { isMobileOrTablet } from "../../../../utils/helpers/mediaQueries";

import type { SelectI, SelectOptionT } from "../../../../models/models";
interface CategorySelectorI {
  isForCampaignsPage: boolean;
  setCategory: Dispatch<SetStateAction<SelectOptionT>>;
}

const SELECT_OPTION_HEIGHT = 29;
const DROPDOWN_PADDING_BOTTOM = 10;
const campaignListHeight = SELECT_OPTION_HEIGHT * 2 + DROPDOWN_PADDING_BOTTOM;
const deliveryListHeight = (length: number) =>
  length * SELECT_OPTION_HEIGHT + DROPDOWN_PADDING_BOTTOM;

const CategorySelector = memo(
  forwardRef((props: CategorySelectorI, ref) => {
    const { isForCampaignsPage, setCategory } = props;

    const { t } = useTranslate();
    const lang = useAppSelector(selectCurrentLang);
    const { categories, selectedCategory, selectCategory } =
      useCategory(isForCampaignsPage);

    useEffect(() => {
      // sync local state with parent state
      setCategory(selectedCategory);
    }, [selectedCategory]);

    const handleChangeCategory = (
      selectedCategory: SelectOptionT,
      actionMeta: ActionMeta<SelectOptionT>
    ) => {
      selectCategory(selectedCategory, actionMeta);
    };

    return (
      <Select
        ref={ref as RefObject<SelectInstance<SelectI>>}
        options={categories}
        value={selectedCategory}
        onChange={handleChangeCategory}
        getOptionLabel={({ label }: SelectOptionT) => t(label)}
        menuPortalTarget={isMobileOrTablet() ? document.body : undefined}
        styles={{
          ...customSearchCategorySelectStyles(lang),
          ...(isMobileOrTablet() && {
            menu: (provided: any, state: any) => ({
              ...customSearchCategorySelectStyles(lang).menu(provided, state),
              zIndex: 99999,
            }),
            menuPortal: (provided: any) => ({
              ...provided,
              zIndex: 99999,
            }),
          }),
          menuList: (provided) => ({
            ...provided,
            minHeight: isForCampaignsPage
              ? campaignListHeight
              : deliveryListHeight(categories.length),
          }),
        }}
        isSearchable={false}
        isRtl={lang === LangE.HE}
        /**
         * Without this react-select renders its OWN built-in placeholder, the hard-coded English
         * literal "Select..." — which never goes through `t()`, so the Hebrew UI showed English
         * too. (There is a `select` Const in Translations, EN "Select..." / HE "בחר...", but
         * react-select never asks for it; the row is a leftover.) `Search in...` names what the
         * control actually does: pick the field the query runs against.
         */
        placeholder={t("Search in...")}
        components={{
          DropdownIndicator,
        }}
      />
    );
  })
);

export default CategorySelector;
